import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, input, output, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { APP_MENU, AppMenuItem } from '../../core/config/navigation.config';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  readonly collapsed = input(false);
  readonly mobileOpen = input(false);
  readonly closeMobile = output<void>();
  protected readonly menuItems = APP_MENU;
  protected readonly expandedMenus = signal<Record<string, boolean>>({});
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly router: Router) {}

  ngOnInit(): void {
    this.expandParentsForUrl(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe((event) => this.expandParentsForUrl(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasChildren(item: AppMenuItem): boolean {
    return Boolean(item.children?.length);
  }

  isExpanded(item: AppMenuItem): boolean {
    return Boolean(this.expandedMenus()[item.label]);
  }

  toggleMenu(item: AppMenuItem): void {
    if (!this.hasChildren(item)) {
      return;
    }

    this.expandedMenus.update((value) => ({ ...value, [item.label]: !value[item.label] }));
  }

  closeOnNavigate(): void {
    this.closeMobile.emit();
  }

  isParentActive(item: AppMenuItem): boolean {
    const cleanUrl = this.router.url.split('?')[0].split('#')[0];
    if (item.children?.some((child) => child.route && cleanUrl.startsWith(this.getRouteSection(child.route)))) {
      return true;
    }

    return Boolean(item.children?.some((child) => child.route && this.router.isActive(child.route, {
      paths: child.exact ? 'exact' : 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    })));
  }

  private getRouteSection(route: string): string {
    const parts = route.split('/').filter(Boolean);
    return parts.length ? `/${parts[0]}` : route;
  }

  private expandParentsForUrl(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const expanded = { ...this.expandedMenus() };

    for (const item of this.menuItems) {
      if (item.children?.some((child) => child.route && cleanUrl.startsWith(child.route))) {
        expanded[item.label] = true;
      }
    }

    if (cleanUrl.startsWith('/user-management/view/') || cleanUrl.startsWith('/user-management/edit/')) {
      expanded['User Management'] = true;
    }

    this.expandedMenus.set(expanded);
  }
}
